<template>
    <application-layout>
        <v-main>
            <v-container>
                <h1 class="mb-4">"{{ surveyName }}" Survey Responses</h1>

                <v-col class="subheading ma-1">Filter results by...</v-col>

                <v-row class="align-center mb-4">
                    <v-col cols="2">
                        <v-select
                            data-testid="survey-response-facility-select"
                            class="survey-response-facility-select mr-2"
                            label="Facility"
                            :items="facilities"
                            filled
                            clearable
                            v-model="searchParams.facility_ids"
                            item-text="location_code"
                            item-value="id"
                            multiple
                            hide-details
                        />
                    </v-col>
                    <v-col cols="2">
                        <app-input
                            data-testid="survey-response-first-name-input"
                            class="survey-response-first-name-input mx-2"
                            :type="'text'"
                            label="First Name"
                            hide-details
                            v-model="searchParams.first_name"
                        />
                    </v-col>
                    <v-col cols="2">
                        <app-input
                            data-testid="survey-response-last-name-input"
                            class="survey-response-last-name-input mx-2"
                            :type="'text'"
                            label="Last Name"
                            hide-details
                            v-model="searchParams.last_name"
                        />
                    </v-col>
                    <v-col cols="2">
                        <v-menu
                            ref="searchStartDate"
                            v-model="searchStartDate"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            :return-value.sync="searchParams.start_date"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    data-testid="survey-response-start-date-input"
                                    class="survey-response-start-date-input mx-2"
                                    v-model="searchParams.start_date"
                                    label="Date from"
                                    readonly
                                    v-on="on"
                                    filled
                                    clearable
                                    hide-details
                                />
                            </template>
                            <v-date-picker
                                v-model="searchParams.start_date"
                                :min="minDate"
                                :max="currentDate"
                                no-title
                                scrollable
                            >
                                <v-spacer />
                                <v-btn
                                    data-testid="survey-response-start-date-cancel-btn"
                                    class="survey-response-start-date-cancel-btn"
                                    text
                                    color="primary"
                                    @click="searchStartDate = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    data-testid="survey-response-start-date-ok-btn"
                                    class="survey-response-start-date-ok-btn"
                                    text
                                    color="primary"
                                    @click="$refs.searchStartDate.save(searchParams.start_date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="2">
                        <v-menu
                            data-testid="survey-response-end-date-menu"
                            class="survey-response-end-date-menu"
                            ref="searchEndDate"
                            v-model="searchEndDate"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            :return-value.sync="searchParams.end_date"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    data-testid="survey-response-end-date-input"
                                    class="survey-response-end-date-input mx-2"
                                    v-model="searchParams.end_date"
                                    label="Date to"
                                    readonly
                                    v-on="on"
                                    filled
                                    clearable
                                    hide-details
                                />
                            </template>
                            <v-date-picker
                                data-testid="survey-response-end-date-date-picker"
                                class="survey-response-end-date-date-picker"
                                v-model="searchParams.end_date"
                                :min="minDate"
                                :max="currentDate"
                                no-title
                                scrollable
                            >
                                <v-spacer />
                                <v-btn
                                    data-testid="survey-response-end-date-cancel-btn"
                                    class="survey-response-end-date-cancel-btn"
                                    text
                                    color="primary"
                                    @click="searchEndDate = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    data-testid="survey-response-end-date-ok-btn"
                                    class="survey-response-end-date-ok-btn"
                                    text
                                    color="primary"
                                    @click="$refs.searchEndDate.save(searchParams.end_date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="2">
                        <v-checkbox
                            data-testid="survey-response-completed-surveys-checkbox"
                            class="survey-response-completed-surveys-checkbox mx-3 mb-3"
                            v-model="searchParams.completed"
                            label="Only Completed Surveys"
                            hide-details
                        />
                    </v-col>
                </v-row>
                <v-btn
                  data-testid="survey-response-search-btn"
                  class="survey-response-search-btn my-4"
                  color="primary"
                  block
                  @click="filterSurveyResponses"
                >
                    <v-icon class="mr-2">search</v-icon>
                    Search
                </v-btn>

                <v-data-table
                    single-expand
                    show-expand
                    :footer-props="{
                      ...footerProps,
                      itemsPerPageOptions: [25, 50, 75, 100, 150, 200],
                    }"
                    item-key="tenant_id"
                    :headers="headers"
                    :items="selectedResponses"
                    :items-per-page="pagination.itemsPerPage"
                    :loading="loading"
                    :no-data-text="loading ? 'Retrieving surveys...' : 'Your search did not return any results.'"
                    :options.sync="pagination"
                    :server-items-length="totalFilteredResponses"
                >
                    <template v-slot:item.data-table-expand="{ isExpanded, expand }">
                        <v-btn
                          data-testid="survey-response-expand-btn"
                          class="survey-response-expand-btn"
                          icon
                          @click="toggleExpand(isExpanded, expand)"
                        >
                            <v-icon v-if="isExpanded">arrow_upward</v-icon>
                            <v-icon v-else>arrow_downward</v-icon>
                        </v-btn>
                    </template>
                    <template v-slot:item.updated_at="{ item }">
                        <span>{{ formatDateAndTime(item.updated_at) }}</span>
                    </template>

                    <template v-slot:expanded-item="{ item }">
                        <td colspan="12">
                            <v-row v-if="item.response">
                                <v-col>
                                    <v-row class="subheading font-weight-bold mx-4 mb-2">
                                        <v-col cols="6">Question</v-col>
                                        <v-col cols="6">Answer</v-col>
                                    </v-row>
                                    <v-divider class="mx-3 mb-3" />
                                    <v-row
                                        v-for="(answer, question, i) in item.response"
                                        :key="i"
                                        class="subheading mx-4 mb-2"
                                    >
                                        <v-col cols="6">
                                            {{ getQuestion(question, item.survey_config) }}
                                        </v-col>
                                        <v-col cols="6">
                                            {{ getAnswer(answer, getQuestion(question, item.survey_config), item.survey_config) }}
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                            <v-row v-else class="subheading mx-3">
                                Survey not completed.
                            </v-row>
                        </td>
                    </template>
                </v-data-table>

                <v-col v-if="hasParams(lastSearch)" class="mt-4">
                    Showing filtered results for
                    <span v-for="(val, key) in lastSearch" :key="key" class="font-weight-bold">
                        <span v-if="val && val.length && key == 'facility_ids'">
                            Facilities:
                            <span v-for="(facilityId, i) in val" :key="i" class="info--text">
                                {{ getFacilityLocationCode(facilityId) }}<span v-if="i < val.length - 1">, </span>
                            </span>
                        </span>
                        <span v-if="val && key == 'first_name'">
                            First Name: <span class="info--text">{{ val }}</span>
                        </span>
                        <span v-if="val && key == 'last_name'">
                            Last Name: <span class="info--text">{{ val }}</span>
                        </span>
                        <span v-if="val && key == 'start_date'">
                            Date From: <span class="info--text">{{ formatDate(val) }}</span>
                        </span>
                        <span v-if="val && key == 'end_date'">
                            Date To: <span class="info--text">{{ formatDate(val) }}</span>
                        </span>
                        <span v-if="val && key == 'completed'">
                            Completed: <span class="info--text">True</span>
                        </span>
                    </span>
                </v-col>

                <v-btn
                  data-testid="survey-response-download-btn"
                  class="survey-response-download-btn error mt-4"
                  block
                  @click="downloadSurveyResponses"
                >
                    <v-icon class="mr-2">save_alt</v-icon>
                    Download
                    <span v-if="hasParams(lastSearch)">&nbsp;Filtered</span>
                    <span v-else>&nbsp;All</span>
                    &nbsp;Surveys
                </v-btn>
            </v-container>
        </v-main>
    </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout.vue";
import { footerProps } from "@/helpers/footer-props";
import { formatDate } from "@/helpers/functions/format-date";
import { formatDateAndTime } from "@/helpers/functions/format-date-time";
import { mapState } from "vuex";
import moment from "moment";
import router from "@/router";
import { surveyResponsesHeaders } from "@/helpers/surveys/survey-responses-data-table-headers";

export default {
  data() {
    return {
      footerProps: footerProps,
      loading: false,
      expand: false,
      searchStartDate: false,
      searchEndDate: false,
      hasSearched: false,
      headers: surveyResponsesHeaders,
      searchParams: {
        facility_ids: [],
        survey_id: router.currentRoute.params.surveyId,
        start_date: "",
        end_date: "",
        completed: "",
        first_name: "",
        last_name: ""
      },
      lastSearch: {},
      filteredResponses: [],
      currentDate: new Date().toISOString().substr(0, 10)
    };
  },

  methods: {
    formatDate: formatDate,
    formatDateAndTime: formatDateAndTime,

    filterSurveyResponses() {
      this.loading = true;
      this.searchParams.completed ? this.searchParams.completed = 1 : this.searchParams.completed = "";
      const sortingOrder = this.pagination.sortDesc[0] ? "DESC" : "ASC";

      const paramsPayload = {
        ...this.searchParams,
        page: this.pagination.page,
        per_page: this.pagination.rowsPerPage,
        order_by: this.pagination.sortBy[0],
        order_direction: sortingOrder
      };

      this.$store.dispatch("getSurveyResponses", paramsPayload)
        .then((resp) => {
          this.hasSearched = true;
          Object.assign(this.lastSearch, this.searchParams);
          delete this.lastSearch.survey_id;
          this.filteredResponses = resp.data.responses;
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          return Promise.reject(err);
        });
    },

    downloadSurveyResponses() {
      this.loading = true;
      this.searchParams.completed = 1;
      this.$store.dispatch("downloadSurveyResponses", this.searchParams)
        .then((resp) => {
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          return Promise.reject(err);
        });
    },

    hasParams(lastSearch) {
      const result = [];
      Object.values(lastSearch).forEach(param => {
        if (param && typeof param == "object" && param.length) {
          result.push(param);
        } else if (param && typeof param != "object") {
          result.push(param);
        } else if (param >= 0 && typeof param == "number") {
          result.push(param);
        }
      });
      return result.length;
    },

    getFacilityLocationCode(facilityId) {
      if (this.facilities && this.facilities.length) {
        return this.facilities.find(facility => facility.id == facilityId).location_code;
      }
    },

    getQuestion(question, config) {
      const questionIndex = question.substr(question.indexOf("_") + 1, question.length);
      question = config.filter(params => params.id == questionIndex)[0].question;
      return question;
    },

    getAnswer(answer, question, config) {
      const filteredQuestion = config.filter(params => params.question == question)[0];
      if (filteredQuestion.type == "multiple_choice") {
        answer = filteredQuestion.allowed_answers.filter(allowedAnswer => allowedAnswer.number == answer)[0].value;
      }
      return answer;
    },

    toggleExpand(isExpanded, expand) {
      !isExpanded ? expand(true) : expand(false);
    }
  },

  computed: {
    ...mapState({
      surveyName: state => state.surveys.surveyName,
      responses: state => state.surveys.surveyResponses
    }),

    minDate() {
      return moment().month(0).date(1).year(2016).format("YYYY-MM-DD");
    },

    facilities() {
      return this.$store.state.facility.facilities
        // eslint-disable-next-line
        ? this.$store.state.facility.facilities.map(facility => facility = { id: facility.id, location_code: facility.location_code })
        : [];
    },

    totalFilteredResponses() {
      return this.filteredResponses ? this.filteredResponses.total : 0;
    },

    selectedResponses() {
      return this.filteredResponses && this.filteredResponses.length ? this.filteredResponses : this.responses;
    },

    darkTheme() {
      return this.$vuetify.theme.dark;
    },

    pagination: {
      get() {
        return this.$store.state.surveys.pagination;
      },
      set(newValue) {
        this.$store.commit("setSurveyPagination", newValue);
      }
    }
  },

  async created() {
    this.loading = true;
    await this.$store.dispatch("getSurveyResponses", { survey_id: this.$route.params.surveyId });
    await this.$store.dispatch("getAllFacilities", { per_page: 999 });
    this.loading = false;
  },

  // ===== Watchers ===== //
  watch: {
    pagination: {
      handler() {
        this.filterSurveyResponses();
      },
      deep: true
    }
  },

  components: {
    ApplicationLayout
  }
};
</script>
